import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AssignableMember } from "./roles-permissions/AssignMembersModal";
import RoleDetailsFlyin from "./roles-permissions/RoleDetailsFlyin";
import RolesPermissionsGrid from "./roles-permissions/RolesPermissionsGrid";
import RolesPermissionsHeader from "./roles-permissions/RolesPermissionsHeader";
import RolesPermissionsModals from "./roles-permissions/RolesPermissionsModals";
import RemoveMemberFromRoleModal from "./roles-permissions/RemoveMemberFromRoleModal";
import RolesSelectionModal from "./members/RolesSelectionModal";
import { TeamRole } from "../types";
import { useRoles } from "../../../shared/api/hooks/useRoles";
import { usePermissions } from "../../../shared/api/hooks/usePermissions";
import { useAdmins } from "../../../shared/api/hooks/useAdmins";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import {
  getApiErrorMessage,
  showTeamManagementErrorToast,
  showTeamManagementSuccessToast,
} from "../../../shared/lib";
import {
  removeAdminRole,
  updateAdminRoleAndPermissions,
} from "../../../shared/api/admins";
import { createRole, deleteRole, updateRole } from "../../../shared/api/roles";
import {
  getBufferedPageOffset,
  getBufferedRequestLimit,
  getBufferedRequestPage,
  getUiTotalPages,
} from "../../../shared/lib/bufferedPagination";
import { RootState } from "../../../redux/store";


const RolesPermissionsSection = () => {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 400);
  const [sort, setSort] = useState("recent");
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [isDeleteRoleOpen, setIsDeleteRoleOpen] = useState(false);
  const [isDeleteRolesModalOpen, setIsDeleteRolesModalOpen] = useState(false);
  const [isPermissionsOpen, setIsPermissionsOpen] = useState(false);
  const [isAssignMembersOpen, setIsAssignMembersOpen] = useState(false);
  const [selectedMemberIdsForRoleAssign, setSelectedMemberIdsForRoleAssign] =
    useState<string[]>([]);
  const [roleAssignMemberQuery, setRoleAssignMemberQuery] = useState("");
  const [isAssigningMembers, setIsAssigningMembers] = useState(false);
  const [assignMembersError, setAssignMembersError] = useState<string | null>(
    null,
  );
  const [permissionQuery, setPermissionQuery] = useState("");
  const [expandedGroupId, setExpandedGroupId] = useState("operations");
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [activeRole, setActiveRole] = useState<TeamRole | null>(null);
  const [isRoleFlyinOpen, setIsRoleFlyinOpen] = useState(false);
  const [showAllFlyinPermissions, setShowAllFlyinPermissions] = useState(false);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [createRoleError, setCreateRoleError] = useState<string | null>(null);
  const [updateRoleError, setUpdateRoleError] = useState<string | null>(null);
  const [deleteRoleError, setDeleteRoleError] = useState<string | null>(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const [rolePendingDeletion, setRolePendingDeletion] = useState<TeamRole | null>(
    null,
  );
  const [memberPendingRoleRemoval, setMemberPendingRoleRemoval] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isRemoveMemberFromRoleOpen, setIsRemoveMemberFromRoleOpen] =
    useState(false);
  const [isRemoveMemberRoleSelectionOpen, setIsRemoveMemberRoleSelectionOpen] =
    useState(false);
  const [memberRoleReassignmentId, setMemberRoleReassignmentId] = useState("");
  const [removeMemberFromRoleError, setRemoveMemberFromRoleError] = useState<
    string | null
  >(null);
  const [isUpdatingMemberRoleAction, setIsUpdatingMemberRoleAction] =
    useState(false);
  const [reassignmentRoleId, setReassignmentRoleId] = useState("");
  const [optimisticRoles, setOptimisticRoles] = useState<TeamRole[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rolesPerPage =
    useSelector((state: RootState) => state.totalItemPerpage) || 20;
  const rolesRequestLimit = getBufferedRequestLimit(rolesPerPage);
  const rolesRequestPage = getBufferedRequestPage(currentPage);

  const {
    roles: apiRoles,
    meta: rolesMeta,
    isLoading: isRolesLoading,
    error: rolesError,
    refetch: refetchRoles,
  } = useRoles({
    q: debouncedQuery.trim() || undefined,
    sort,
    page: rolesRequestPage,
    limit: rolesRequestLimit,
  });
  const {
    permissionGroups,
    isLoading: isPermissionsLoading,
    error: permissionsError,
  } = usePermissions();
  const { admins, refetch: refetchAdmins } = useAdmins();

  const toInitials = (label: string) => {
    const parts = label.trim().split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return `${a}${b}`.toUpperCase();
  };

  const assignableMembers = useMemo<AssignableMember[]>(() => {
    return admins.map((admin) => {
      const fullName = `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim();
      const displayName =
        fullName || admin.email?.split("@")[0] || "Unknown member";
      const initials = toInitials(displayName);

      return {
        id: admin._id,
        name: displayName,
        label: initials || "NA",
      };
    });
  }, [admins]);

  const availableMembersForActiveRole = useMemo(() => {
    if (!activeRole) return assignableMembers;
    const assignedMemberIds = new Set(activeRole.members.map((member) => member.id));
    return assignableMembers.filter((member) => !assignedMemberIds.has(member.id));
  }, [activeRole, assignableMembers]);

  const adminsById = useMemo(() => {
    return new Map(admins.map((admin) => [admin._id, admin]));
  }, [admins]);

  const apiRolesAsTeamRoles: TeamRole[] = useMemo(() => {
    return apiRoles.map((role) => ({
      id: role.id,
      name: role.name,
      description: role.description ?? "No description",
      permissions: role.permissions ?? [],
      members: (role.admins ?? []).map((admin) => {
        const fullName = `${admin.firstName ?? ""} ${admin.lastName ?? ""}`.trim();
        const displayName = fullName || admin.name || admin.email || "Unknown member";
        const initials = toInitials(displayName);
        return {
          id: admin._id,
          label: initials || "NA",
          name: displayName,
        };
      }),
    }));
  }, [apiRoles]);

  useEffect(() => {
    setOptimisticRoles((prev) => {
      if (prev.length === 0) return prev;
      const apiRoleNames = new Set(
        apiRolesAsTeamRoles.map((role) => role.name.trim().toLowerCase()),
      );
      const next = prev.filter(
        (role) => !apiRoleNames.has(role.name.trim().toLowerCase()),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [apiRolesAsTeamRoles]);

  const rolesForUi = useMemo(() => {
    const nextRoles = [...optimisticRoles];
    const existingByName = new Set(
      nextRoles.map((role) => role.name.trim().toLowerCase()),
    );

    apiRolesAsTeamRoles.forEach((role) => {
      if (existingByName.has(role.name.trim().toLowerCase())) return;
      nextRoles.push(role);
    });

    return nextRoles;
  }, [apiRolesAsTeamRoles, optimisticRoles]);

  const filteredRoles = useMemo(() => {
    return rolesForUi;
  }, [rolesForUi]);

  const totalRolePages = getUiTotalPages(
    rolesMeta,
    rolesPerPage,
    rolesRequestLimit,
    filteredRoles.length,
  );
  const pagedRoles = useMemo(() => {
    const start = getBufferedPageOffset(currentPage, rolesPerPage);
    return filteredRoles.slice(start, start + rolesPerPage);
  }, [currentPage, filteredRoles, rolesPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, sort]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalRolePages));
  }, [totalRolePages]);

  const filteredPermissionGroups = useMemo(() => {
    if (permissionGroups.length === 0) return [];
    if (!permissionQuery) return permissionGroups;
    const loweredQuery = permissionQuery.toLowerCase();
    return permissionGroups
      .map((group) => {
        const matchesGroup = group.label.toLowerCase().includes(loweredQuery);
        const matchedPermissions = group.permissions.filter((permission) =>
          permission.toLowerCase().includes(loweredQuery),
        );
        if (!matchesGroup && matchedPermissions.length === 0) return null;
        return {
          ...group,
          permissions: matchesGroup ? group.permissions : matchedPermissions,
        };
      })
      .filter(Boolean) as typeof permissionGroups;
  }, [permissionGroups, permissionQuery]);

  const togglePermission = (permission: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((item) => item !== permission)
        : [...prev, permission],
    );
  };

  const toggleGroupPermissions = (groupPermissions: string[]) => {
    const isAllSelected = groupPermissions.every((permission) =>
      selectedPermissions.includes(permission),
    );
    if (isAllSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((permission) => !groupPermissions.includes(permission)),
      );
      return;
    }
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      groupPermissions.forEach((permission) => next.add(permission));
      return Array.from(next);
    });
  };

  const trimmedRoleName = newRoleName.trim();
  const trimmedRoleDescription = newRoleDescription.trim();
  const isRoleFormValid =
    trimmedRoleName.length > 0 &&
    trimmedRoleDescription.length > 0 &&
    selectedPermissions.length > 0;
  const isCreateRoleFormValid = isRoleFormValid;
  const isEditRoleFormValid = isRoleFormValid && !!editingRoleId;
  const createRoleValidationMessage = !isCreateRoleFormValid
    ? "Role name, description, and at least one permission are required."
    : null;
  const editRoleValidationMessage = !isEditRoleFormValid
    ? "Role name, description, and at least one permission are required."
    : null;

  const getCreateRoleErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to create role. Please try again.");
  };

  const showErrorToast = (message: string) => {
    showTeamManagementErrorToast(message);
  };

  const openCreateRoleModal = () => {
    setNewRoleName("");
    setNewRoleDescription("");
    setSelectedPermissions([]);
    setEditingRoleId(null);
    setCreateRoleError(null);
    setUpdateRoleError(null);
    setIsCreateRoleOpen(true);
  };

  const openEditRoleModal = (role: TeamRole) => {
    setEditingRoleId(role.id);
    setNewRoleName(role.name);
    setNewRoleDescription(role.description);
    setSelectedPermissions(role.permissions);
    setUpdateRoleError(null);
    setIsEditRoleOpen(true);
  };

  const openRoleFlyin = (role: TeamRole) => {
    setActiveRole(role);
    setShowAllFlyinPermissions(false);
    setIsRoleFlyinOpen(true);
  };

  const openDeleteRoleModal = (role: TeamRole) => {
    setRolePendingDeletion(role);
    setReassignmentRoleId("");
    setDeleteRoleError(null);
    setIsDeleteRolesModalOpen(false);
    setIsDeleteRoleOpen(true);
  };

  const openAssignMembersModal = (role: TeamRole) => {
    setActiveRole(role);
    setSelectedMemberIdsForRoleAssign([]);
    setRoleAssignMemberQuery("");
    setAssignMembersError(null);
    setIsAssignMembersOpen(true);
  };

  const openRemoveMemberFromRoleModal = (memberId: string) => {
    if (!activeRole) return;
    const targetMember = activeRole.members.find((member) => member.id === memberId);
    if (!targetMember) return;

    setMemberPendingRoleRemoval({
      id: targetMember.id,
      name: targetMember.name,
    });
    setMemberRoleReassignmentId("");
    setRemoveMemberFromRoleError(null);
    setIsRemoveMemberRoleSelectionOpen(false);
    setIsRemoveMemberFromRoleOpen(true);
  };

  const handleCreateRole = async () => {
    if (!isCreateRoleFormValid || isCreatingRole) return;

    setCreateRoleError(null);
    setIsCreatingRole(true);

    try {
      const optimisticRole: TeamRole = {
        id: `temp-role-${Date.now()}`,
        name: trimmedRoleName,
        description: trimmedRoleDescription,
        permissions: selectedPermissions,
        members: [],
      };

      await createRole({
        name: trimmedRoleName,
        description: trimmedRoleDescription,
        permissions: selectedPermissions,
      });

      setOptimisticRoles((prev) => [
        optimisticRole,
        ...prev.filter(
          (role) =>
            role.name.trim().toLowerCase() !==
            optimisticRole.name.trim().toLowerCase(),
        ),
      ]);
      setIsCreateRoleOpen(false);
      setNewRoleName("");
      setNewRoleDescription("");
      setSelectedPermissions([]);
      refetchRoles();
    } catch (error) {
      const message = getCreateRoleErrorMessage(error);
      setCreateRoleError(message);
      showErrorToast(message);
    } finally {
      setIsCreatingRole(false);
    }
  };

  const getUpdateRoleErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to update role. Please try again.");
  };

  const handleUpdateRole = async () => {
    if (!isEditRoleFormValid || !editingRoleId || isUpdatingRole) return;

    setUpdateRoleError(null);
    setIsUpdatingRole(true);
    try {
      await updateRole(editingRoleId, {
        name: trimmedRoleName,
        description: trimmedRoleDescription,
        permissions: selectedPermissions,
      });

      setIsEditRoleOpen(false);
      setEditingRoleId(null);
      refetchRoles();
      showTeamManagementSuccessToast("Role updated successfully!");
    } catch (error) {
      const message = getUpdateRoleErrorMessage(error);
      setUpdateRoleError(message);
      showErrorToast(message);
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const toggleMemberSelectionForRoleAssign = (memberId: string) => {
    setSelectedMemberIdsForRoleAssign((prev) =>
      prev.includes(memberId)
        ? prev.filter((item) => item !== memberId)
        : [...prev, memberId],
    );
  };

  const getAssignMembersErrorMessage = (error: unknown) => {
    return getApiErrorMessage(
      error,
      "Unable to assign role to selected members. Please try again.",
    );
  };

  const handleAssignMembersToRole = async () => {
    if (!activeRole || selectedMemberIdsForRoleAssign.length === 0) return;
    if (isAssigningMembers) return;

    setAssignMembersError(null);
    setIsAssigningMembers(true);
    try {
      await Promise.all(
        selectedMemberIdsForRoleAssign.map((memberId) =>
          updateAdminRoleAndPermissions(memberId, {
            roleIds: [activeRole.id],
            additionalPermissions: activeRole.permissions,
          }),
        ),
      );

      setIsAssignMembersOpen(false);
      setSelectedMemberIdsForRoleAssign([]);
      setRoleAssignMemberQuery("");
      refetchRoles();
      refetchAdmins();
      showTeamManagementSuccessToast("Role assigned successfully!");
    } catch (error) {
      const message = getAssignMembersErrorMessage(error);
      setAssignMembersError(message);
      showErrorToast(message);
    } finally {
      setIsAssigningMembers(false);
    }
  };

  const getRoleMemberActionErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to update member. Please try again.");
  };

  const handleRemoveMemberFromRole = async () => {
    if (!activeRole || !memberPendingRoleRemoval) return;

    const admin = adminsById.get(memberPendingRoleRemoval.id);
    if (!admin) return;

    setRemoveMemberFromRoleError(null);
    setIsUpdatingMemberRoleAction(true);
    try {
      const rolesExcludingCurrent = (admin.roles ?? [])
        .filter((assignedRole) => {
          const matchesById = assignedRole._id === activeRole.id;
          const matchesByName =
            assignedRole.name?.trim().toLowerCase() ===
            activeRole.name.trim().toLowerCase();
          return !matchesById && !matchesByName;
        })
        .map((assignedRole) => assignedRole._id);

      if (memberRoleReassignmentId) {
        const nextRoleIds = Array.from(
          new Set([...rolesExcludingCurrent, memberRoleReassignmentId]),
        );
        await updateAdminRoleAndPermissions(memberPendingRoleRemoval.id, {
          roleIds: nextRoleIds,
          additionalPermissions: admin.additionalPermissions ?? [],
        });
      } else {
        const roleIdsToRemove = (admin.roles ?? [])
          .filter((assignedRole) => {
            const matchesById = assignedRole._id === activeRole.id;
            const matchesByName =
              assignedRole.name?.trim().toLowerCase() ===
              activeRole.name.trim().toLowerCase();
            return matchesById || matchesByName;
          })
          .map((assignedRole) => assignedRole._id);

        const normalizedRoleIdsToRemove = roleIdsToRemove.length
          ? roleIdsToRemove
          : [activeRole.id];

        await removeAdminRole(memberPendingRoleRemoval.id, {
          roleIds: normalizedRoleIdsToRemove,
        });
      }

      setActiveRole((prev) =>
        prev
          ? {
              ...prev,
              members: prev.members.filter(
                (member) => member.id !== memberPendingRoleRemoval.id,
              ),
            }
          : prev,
      );
      setIsRemoveMemberFromRoleOpen(false);
      setIsRemoveMemberRoleSelectionOpen(false);
      setMemberPendingRoleRemoval(null);
      setMemberRoleReassignmentId("");
      setRemoveMemberFromRoleError(null);
      refetchRoles();
      refetchAdmins();
      showTeamManagementSuccessToast(
        memberRoleReassignmentId
          ? "Member reassigned successfully!"
          : "Member removed from role.",
      );
    } catch (error) {
      const message = getRoleMemberActionErrorMessage(error);
      setRemoveMemberFromRoleError(message);
      showErrorToast(message);
    } finally {
      setIsUpdatingMemberRoleAction(false);
    }
  };

  const membersNeedingReassignment = useMemo(() => {
    if (!rolePendingDeletion) return 0;

    return rolePendingDeletion.members.reduce((count, member) => {
      const admin = adminsById.get(member.id);

      if (!admin || !Array.isArray(admin.roles) || admin.roles.length === 0) {
        return count + 1;
      }

      const hasAnotherRole = admin.roles.some((assignedRole) => {
        const matchesById = assignedRole._id === rolePendingDeletion.id;
        const matchesByName =
          assignedRole.name?.trim().toLowerCase() ===
          rolePendingDeletion.name.trim().toLowerCase();
        return !matchesById && !matchesByName;
      });

      return hasAnotherRole ? count : count + 1;
    }, 0);
  }, [adminsById, rolePendingDeletion]);

  const reassignmentRoleOptions = useMemo(() => {
    if (!rolePendingDeletion) return [];
    return rolesForUi
      .filter((role) => role.id !== rolePendingDeletion.id)
      .map((role) => ({ label: role.name, value: role.id }));
  }, [rolePendingDeletion, rolesForUi]);

  const reassignmentRoleOptionsForModal = useMemo(() => {
    return reassignmentRoleOptions.map((role) => ({
      id: role.value,
      name: role.label,
      description: "",
      permissions: [],
    }));
  }, [reassignmentRoleOptions]);

  const selectedReassignmentRoleName = useMemo(() => {
    const selected = reassignmentRoleOptions.find(
      (role) => role.value === reassignmentRoleId,
    );
    return selected?.label ?? "";
  }, [reassignmentRoleId, reassignmentRoleOptions]);

  const toggleDeleteReassignmentRole = (roleName: string) => {
    const selected = reassignmentRoleOptions.find(
      (role) => role.label === roleName,
    );
    if (!selected) return;
    setReassignmentRoleId((prev) => (prev === selected.value ? "" : selected.value));
  };

  const memberRoleReassignmentOptions = useMemo(() => {
    if (!activeRole) return [];
    return rolesForUi
      .filter((role) => role.id !== activeRole.id)
      .map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        permissions: [],
      }));
  }, [activeRole, rolesForUi]);

  const selectedMemberRoleReassignmentName = useMemo(() => {
    const selected = memberRoleReassignmentOptions.find(
      (role) => role.id === memberRoleReassignmentId,
    );
    return selected?.name ?? "";
  }, [memberRoleReassignmentId, memberRoleReassignmentOptions]);

  const toggleMemberRoleReassignment = (roleName: string) => {
    const selected = memberRoleReassignmentOptions.find(
      (role) => role.name === roleName,
    );
    if (!selected) return;
    setMemberRoleReassignmentId((prev) =>
      prev === selected.id ? "" : selected.id,
    );
  };

  const getDeleteRoleErrorMessage = (error: unknown) => {
    return getApiErrorMessage(error, "Unable to delete role. Please try again.");
  };

  const handleDeleteRole = async () => {
    if (!rolePendingDeletion || isDeletingRole) return;

    const requiresReassignment = membersNeedingReassignment > 0;
    if (requiresReassignment && !reassignmentRoleId) return;

    setDeleteRoleError(null);
    setIsDeletingRole(true);
    try {
      await deleteRole(rolePendingDeletion.id, {
        roleId: requiresReassignment ? reassignmentRoleId : "",
      });

      setIsDeleteRoleOpen(false);
      setRolePendingDeletion(null);
      setReassignmentRoleId("");
      if (activeRole?.id === rolePendingDeletion.id) {
        setIsRoleFlyinOpen(false);
        setActiveRole(null);
      }
      refetchRoles();
      refetchAdmins();
      showTeamManagementSuccessToast("Role deleted successfully!");
    } catch (error) {
      const message = getDeleteRoleErrorMessage(error);
      setDeleteRoleError(message);
      showErrorToast(message);
    } finally {
      setIsDeletingRole(false);
    }
  };

  return (
    <>
      <RolesPermissionsHeader
        query={query}
        sort={sort}
        onQueryChange={setQuery}
        onSortChange={setSort}
        onCreateRole={openCreateRoleModal}
      />

      <RolesPermissionsGrid
        isLoading={isRolesLoading}
        error={rolesError}
        filteredRolesCount={filteredRoles.length}
        pagedRoles={pagedRoles}
        currentPage={currentPage}
        totalPages={totalRolePages}
        onPageChange={setCurrentPage}
        onEditRole={openEditRoleModal}
        onAddMembers={openAssignMembersModal}
        onDeleteRole={openDeleteRoleModal}
        onOpenDetails={openRoleFlyin}
      />

      <RoleDetailsFlyin
        open={isRoleFlyinOpen}
        role={activeRole}
        showAllPermissions={showAllFlyinPermissions}
        onOpenChange={setIsRoleFlyinOpen}
        onToggleShowAllPermissions={() =>
          setShowAllFlyinPermissions((prev) => !prev)
        }
        onAssignMembers={openAssignMembersModal}
        onEditRole={openEditRoleModal}
        onDeleteRole={openDeleteRoleModal}
        onRemoveMemberFromRole={openRemoveMemberFromRoleModal}
      />

      <RemoveMemberFromRoleModal
        open={isRemoveMemberFromRoleOpen}
        onOpenChange={(open) => {
          setIsRemoveMemberFromRoleOpen(open);
          if (!open && !isUpdatingMemberRoleAction) {
            setIsRemoveMemberRoleSelectionOpen(false);
            setMemberPendingRoleRemoval(null);
            setMemberRoleReassignmentId("");
            setRemoveMemberFromRoleError(null);
          }
        }}
        memberName={memberPendingRoleRemoval?.name ?? "Member"}
        reassignmentRoleName={selectedMemberRoleReassignmentName}
        canChooseReassignment={memberRoleReassignmentOptions.length > 0}
        onOpenReassignmentRolesModal={() => {
          if (isUpdatingMemberRoleAction) return;
          setIsRemoveMemberRoleSelectionOpen(true);
        }}
        onCancel={() => {
          if (isUpdatingMemberRoleAction) return;
          setIsRemoveMemberFromRoleOpen(false);
          setIsRemoveMemberRoleSelectionOpen(false);
          setMemberPendingRoleRemoval(null);
          setMemberRoleReassignmentId("");
          setRemoveMemberFromRoleError(null);
        }}
        onRemoveRole={handleRemoveMemberFromRole}
        isSubmitting={isUpdatingMemberRoleAction}
        error={removeMemberFromRoleError}
      />

      <RolesSelectionModal
        open={isRemoveMemberRoleSelectionOpen}
        onOpenChange={setIsRemoveMemberRoleSelectionOpen}
        roles={memberRoleReassignmentOptions}
        selectedRoles={
          selectedMemberRoleReassignmentName
            ? [selectedMemberRoleReassignmentName]
            : []
        }
        onToggleRoleSelection={toggleMemberRoleReassignment}
        className="z-[60]"
      />

      <RolesPermissionsModals
        isCreateRoleOpen={isCreateRoleOpen}
        isEditRoleOpen={isEditRoleOpen}
        isPermissionsOpen={isPermissionsOpen}
        isAssignMembersOpen={isAssignMembersOpen}
        isDeleteRoleOpen={isDeleteRoleOpen}
        isDeleteRolesModalOpen={isDeleteRolesModalOpen}
        isCreateRoleFormValid={isCreateRoleFormValid}
        isEditRoleFormValid={isEditRoleFormValid}
        isCreatingRole={isCreatingRole}
        isUpdatingRole={isUpdatingRole}
        isAssigningMembers={isAssigningMembers}
        isDeletingRole={isDeletingRole}
        newRoleName={newRoleName}
        newRoleDescription={newRoleDescription}
        selectedPermissions={selectedPermissions}
        createRoleError={createRoleError}
        createRoleValidationMessage={createRoleValidationMessage}
        updateRoleError={updateRoleError}
        editRoleValidationMessage={editRoleValidationMessage}
        permissionQuery={permissionQuery}
        filteredPermissionGroups={filteredPermissionGroups}
        isPermissionsLoading={isPermissionsLoading}
        permissionsError={permissionsError}
        expandedGroupId={expandedGroupId}
        activeRoleName={activeRole?.name ?? ""}
        availableMembersForActiveRole={availableMembersForActiveRole}
        selectedMemberIdsForRoleAssign={selectedMemberIdsForRoleAssign}
        roleAssignMemberQuery={roleAssignMemberQuery}
        assignMembersError={assignMembersError}
        rolePendingDeletionName={rolePendingDeletion?.name ?? "this role"}
        membersNeedingReassignment={membersNeedingReassignment}
        selectedReassignmentRoleName={selectedReassignmentRoleName}
        canChooseReassignment={reassignmentRoleOptions.length > 0}
        deleteRoleError={deleteRoleError}
        reassignmentRoleOptionsForModal={reassignmentRoleOptionsForModal}
        onCreateRoleOpenChange={(open) => {
          setIsCreateRoleOpen(open);
          if (!open) setCreateRoleError(null);
        }}
        onCreateRoleNameChange={setNewRoleName}
        onCreateRoleDescriptionChange={setNewRoleDescription}
        onOpenPermissions={() => {
          setExpandedGroupId("");
          setIsPermissionsOpen(true);
        }}
        onCreateRoleSubmit={handleCreateRole}
        onCreateRoleCancel={() => {
          setCreateRoleError(null);
          setIsCreateRoleOpen(false);
        }}
        onEditRoleOpenChange={(open) => {
          setIsEditRoleOpen(open);
          if (!open) {
            setEditingRoleId(null);
            setUpdateRoleError(null);
          }
        }}
        onEditRoleSubmit={handleUpdateRole}
        onEditRoleCancel={() => {
          if (isUpdatingRole) return;
          setIsEditRoleOpen(false);
          setEditingRoleId(null);
          setUpdateRoleError(null);
        }}
        onPermissionsOpenChange={setIsPermissionsOpen}
        onPermissionQueryChange={setPermissionQuery}
        onExpandedGroupIdChange={setExpandedGroupId}
        isPermissionSelected={(permission) =>
          selectedPermissions.includes(permission)
        }
        onTogglePermission={togglePermission}
        onToggleGroupPermissions={toggleGroupPermissions}
        onAssignMembersOpenChange={setIsAssignMembersOpen}
        onRoleAssignMemberQueryChange={setRoleAssignMemberQuery}
        onToggleMemberSelection={toggleMemberSelectionForRoleAssign}
        onAssignMembers={handleAssignMembersToRole}
        onAssignMembersCancel={() => {
          if (isAssigningMembers) return;
          setIsAssignMembersOpen(false);
          setAssignMembersError(null);
        }}
        onDeleteRoleOpenChange={(open) => {
          setIsDeleteRoleOpen(open);
          if (!open && !isDeletingRole) {
            setRolePendingDeletion(null);
            setReassignmentRoleId("");
            setDeleteRoleError(null);
            setIsDeleteRolesModalOpen(false);
          }
        }}
        onOpenReassignmentRolesModal={() => {
          if (isDeletingRole) return;
          setIsDeleteRolesModalOpen(true);
        }}
        onDeleteRoleCancel={() => {
          if (isDeletingRole) return;
          setIsDeleteRoleOpen(false);
          setRolePendingDeletion(null);
          setReassignmentRoleId("");
          setDeleteRoleError(null);
          setIsDeleteRolesModalOpen(false);
        }}
        onDeleteRole={handleDeleteRole}
        onDeleteRolesSelectionOpenChange={setIsDeleteRolesModalOpen}
        onToggleDeleteReassignmentRole={toggleDeleteReassignmentRole}
      />
    </>
  );
};

export default RolesPermissionsSection;
